export const docs = [
  {
    slug: "copy-text-from-phone-to-pc",
    title: "How to Copy Text From Phone to PC in Seconds",
    description:
      "A fast guide to copy text from your phone to your computer using a browser, QR code, and a private share link.",
    summary:
      "Move clipboard text from mobile to desktop in a few clicks without installing any app.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "4 min",
    keywords: [
      "copy text from phone to pc",
      "transfer clipboard from mobile to desktop",
      "cross device text sharing"
    ],
    sections: [
      {
        heading: "Why this workflow is useful",
        paragraphs: [
          "If you regularly switch between mobile and desktop, manual retyping wastes time and creates mistakes.",
          "A shared link plus live syncing lets you move snippets, codes, and notes instantly between devices."
        ]
      },
      {
        heading: "Step-by-step",
        paragraphs: [
          "Open CrossCopy on your phone and paste the text you want to move.",
          "Open the same link on your computer by scanning the QR code or typing the short URL.",
          "Copy the synced text on desktop. Edit either side and the content stays in sync."
        ]
      },
      {
        heading: "Security tips",
        paragraphs: [
          "Treat shared links like temporary keys. Anyone with the URL can read the content.",
          "Do not share passwords, banking details, or private credentials in a public or reused link."
        ]
      }
    ],
    faqs: [
      {
        question: "Do I need to install an app?",
        answer: "No. CrossCopy works directly in the browser."
      },
      {
        question: "Does this work between iPhone and Windows?",
        answer: "Yes. Any device with a modern browser can join the same link."
      }
    ]
  },
  {
    slug: "share-clipboard-between-android-and-mac",
    title: "Share Clipboard Between Android and Mac",
    description:
      "Use one browser link to sync text between Android phones and Mac computers without native integrations.",
    summary:
      "A practical Android-to-macOS clipboard bridge that works instantly in the browser.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "5 min",
    keywords: [
      "android mac clipboard",
      "share text between android and mac",
      "universal clipboard alternative"
    ],
    sections: [
      {
        heading: "Common Android and Mac limitation",
        paragraphs: [
          "Apple Universal Clipboard does not include Android devices.",
          "A neutral browser tool fills that gap with no vendor lock-in."
        ]
      },
      {
        heading: "Setup",
        paragraphs: [
          "Create or open a CrossCopy route on Android.",
          "Open the same route on your Mac and pin it in a browser tab for repeat use.",
          "Paste text on one side and copy from the other device immediately."
        ]
      },
      {
        heading: "Best practices",
        paragraphs: [
          "Use custom route names that are memorable but not guessable.",
          "Clear old content after transfers to reduce accidental exposure."
        ]
      }
    ],
    faqs: [
      {
        question: "Can I share long code snippets?",
        answer: "Yes, but keep formatting checks in mind after pasting into editors."
      },
      {
        question: "Is this only for Chrome?",
        answer: "No. It works across modern mobile and desktop browsers."
      }
    ]
  },
  {
    slug: "cross-device-clipboard-without-installing-apps",
    title: "Cross-Device Clipboard Without Installing Apps",
    description:
      "Learn how to sync text between devices in a browser-only workflow with no downloads and no account required.",
    summary:
      "Browser-first clipboard sharing for teams and personal workflows.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "4 min",
    keywords: [
      "clipboard without app",
      "no install text sharing",
      "browser clipboard sync"
    ],
    sections: [
      {
        heading: "Why no-install matters",
        paragraphs: [
          "Install restrictions are common on work laptops and shared devices.",
          "A web route bypasses app approvals and keeps setup almost instant."
        ]
      },
      {
        heading: "How to use",
        paragraphs: [
          "Open the same CrossCopy route on both devices.",
          "Paste text into one session and copy from the other.",
          "Bookmark frequently used routes for recurring workflows."
        ]
      }
    ],
    faqs: [
      {
        question: "Do I need an account?",
        answer: "No account is required."
      },
      {
        question: "Can I use this on corporate machines?",
        answer: "Usually yes if browser access is allowed, but follow company policy."
      }
    ]
  },
  {
    slug: "qr-code-text-transfer-workflow",
    title: "QR Code Text Transfer Workflow",
    description:
      "Use QR scanning to open a shared clipboard route quickly across devices and reduce typing mistakes.",
    summary:
      "Fast route handoff from desktop to mobile with QR scan.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "3 min",
    keywords: [
      "qr code text transfer",
      "scan to share clipboard",
      "phone desktop qr text"
    ],
    sections: [
      {
        heading: "When QR is best",
        paragraphs: [
          "QR codes are ideal when URLs are long or you want to avoid typing on mobile.",
          "They are also useful in demos or quick pair sessions."
        ]
      },
      {
        heading: "Workflow",
        paragraphs: [
          "Open a CrossCopy route on desktop.",
          "Scan the QR code with your phone camera.",
          "Start sharing text instantly in the same route."
        ]
      }
    ],
    faqs: [
      {
        question: "Can I use a third device too?",
        answer: "Yes. Multiple devices can open the same route."
      }
    ]
  },
  {
    slug: "safe-clipboard-sharing-best-practices",
    title: "Safe Clipboard Sharing Best Practices",
    description:
      "Security checklist for sharing text between devices, including route hygiene and sensitive-data handling.",
    summary:
      "Practical security habits for browser-based clipboard workflows.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "6 min",
    keywords: [
      "secure clipboard sharing",
      "safe text transfer",
      "clipboard privacy tips"
    ],
    sections: [
      {
        heading: "Threat model basics",
        paragraphs: [
          "Anyone with route access can read shared content.",
          "Plan as if links can leak through history, screenshots, or copied messages."
        ]
      },
      {
        heading: "Checklist",
        paragraphs: [
          "Avoid passwords and API keys in shared routes.",
          "Use unique routes per session.",
          "Clear content after use and close stale tabs."
        ]
      }
    ],
    faqs: [
      {
        question: "Is data encrypted end-to-end?",
        answer: "Assume route-based access control only and avoid sensitive secrets."
      }
    ]
  },
  {
    slug: "crosscopy-vs-alternatives",
    title: "CrossCopy vs Alternatives for Text Sharing",
    description:
      "Compare browser clipboard sharing with native ecosystem tools and messaging apps for quick text transfer.",
    summary:
      "A realistic comparison of convenience, compatibility, and privacy tradeoffs.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "5 min",
    keywords: [
      "crosscopy alternative",
      "airdrop alternative for text",
      "universal clipboard comparison"
    ],
    sections: [
      {
        heading: "Comparison dimensions",
        paragraphs: [
          "Speed, compatibility, setup friction, and privacy controls are the most practical criteria.",
          "No single option wins for every use case, so pick based on device mix and sensitivity."
        ]
      },
      {
        heading: "Where CrossCopy fits",
        paragraphs: [
          "CrossCopy is strongest when devices are from different ecosystems and you need zero install.",
          "Native tools can be smoother inside one ecosystem but usually fail cross-platform."
        ]
      }
    ],
    faqs: [
      {
        question: "Is this better than sending yourself messages?",
        answer: "For quick snippets, a live route is faster and avoids inbox clutter."
      }
    ]
  },
  {
    slug: "clipboard-sync-troubleshooting",
    title: "Clipboard Sync Troubleshooting Guide",
    description:
      "Fix common text sync issues across devices, including stale tabs, route mismatch, and connectivity checks.",
    summary:
      "A quick troubleshooting flow for slow or missing text updates.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "4 min",
    keywords: [
      "clipboard sync not working",
      "cross device text sync delay",
      "crosscopy troubleshooting"
    ],
    sections: [
      {
        heading: "Fast diagnostics",
        paragraphs: [
          "Confirm all devices are on the exact same route URL.",
          "Refresh stale tabs and verify internet connectivity on each device."
        ]
      },
      {
        heading: "Common fixes",
        paragraphs: [
          "Close duplicate tabs that may show outdated content.",
          "Try a new route to rule out stale session issues.",
          "Disable aggressive privacy extensions temporarily if updates are blocked."
        ]
      }
    ],
    faqs: [
      {
        question: "Why do I see old text?",
        answer: "Most often a stale tab or wrong route URL is open on one device."
      }
    ]
  },
  {
    slug: "privacy-focused-clipboard-sharing-guide",
    title: "Privacy-Focused Clipboard Sharing Guide",
    description:
      "Design a privacy-aware workflow for cross-device text sharing with minimal retention and careful link handling.",
    summary:
      "Use route discipline and minimal data exposure to reduce privacy risks.",
    publishedDate: "2026-02-07",
    lastUpdated: "2026-02-07",
    readingTime: "5 min",
    keywords: [
      "privacy clipboard sharing",
      "private text transfer",
      "secure cross device copy paste"
    ],
    sections: [
      {
        heading: "Privacy principles",
        paragraphs: [
          "Share the least amount of text required for the task.",
          "Prefer one-time routes for temporary operations."
        ]
      },
      {
        heading: "Operational habits",
        paragraphs: [
          "Rotate route names for different contexts.",
          "Never post active route URLs in public chats or tickets.",
          "Clear route content when the transfer is complete."
        ]
      }
    ],
    faqs: [
      {
        question: "Can teams use this safely?",
        answer: "Yes, with clear internal policy on route sharing and content sensitivity."
      }
    ]
  }
];

export const docsBySlug = docs.reduce((acc, article) => {
  acc[article.slug] = article;
  return acc;
}, {});
